import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../api/admin.api';
import toast from 'react-hot-toast';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Card,
  CardContent,
  Divider,
  Stack,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Visibility,
  GetApp,
  FilterList,
  CheckCircle,
  Cancel,
  Image,
  Email,
  Phone,
  Close,
  Download,
  Edit,
  Delete,
  ThumbUp,
  ThumbDown,
  Badge,
  Business,
  Person,
} from '@mui/icons-material';

const StateAssociationsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedAssociations, setSelectedAssociations] = useState([]);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [editingAssociation, setEditingAssociation] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAssociationId, setDeleteAssociationId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({});
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState({
    verificationStatus: 'all',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchAssociations();
  }, [isAuthenticated, isAdmin, navigate, filters]);

  const fetchAssociations = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllStateAssociations(filters);
      setAssociations(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load state associations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'page' || key === 'limit') {
      setFilters({ ...filters, [key]: value });
    } else {
      setFilters({ ...filters, [key]: value, page: 1 });
    }
  };

  const handleViewDetails = async (associationId) => {
    try {
      const response = await adminAPI.getStateAssociationDetails(associationId);
      setSelectedAssociation(response.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load association details');
    }
  };

  const handleQuickApprove = async (associationId) => {
    try {
      await adminAPI.verifyStateAssociation(associationId, {
        status: 'approved',
        notes: 'Quick approval from table',
      });
      toast.success('State association approved successfully');
      fetchAssociations();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.message || 'Approval failed');
    }
  };

  const handleQuickReject = async (associationId) => {
    try {
      await adminAPI.verifyStateAssociation(associationId, {
        status: 'rejected',
        notes: 'Quick rejection from table',
      });
      toast.success('State association rejected successfully');
      fetchAssociations();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.message || 'Rejection failed');
    }
  };

  const handleDeleteAssociation = async () => {
    if (!deleteAssociationId) return;
    setDeleting(true);
    try {
      await adminAPI.deleteStateAssociation(deleteAssociationId);
      toast.success('State association deleted successfully');
      setShowDeleteConfirm(false);
      setDeleteAssociationId(null);
      fetchAssociations();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete association');
    } finally {
      setDeleting(false);
    }
  };

  const handleVerifyAssociation = async (associationId, status) => {
    try {
      await adminAPI.verifyStateAssociation(associationId, {
        status,
        notes: verificationNotes,
      });
      toast.success(`State association ${status} successfully`);
      setShowModal(false);
      setVerificationNotes('');
      fetchAssociations();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.message || 'Verification failed');
    }
  };

  const handleBulkVerify = async (status) => {
    if (selectedAssociations.length === 0) {
      toast.error('Please select associations to verify');
      return;
    }

    try {
      await adminAPI.bulkVerifyStateAssociations({
        associationIds: selectedAssociations,
        status,
        notes: verificationNotes,
      });
      toast.success(`${selectedAssociations.length} associations ${status} successfully`);
      setSelectedAssociations([]);
      setShowVerificationModal(false);
      setVerificationNotes('');
      fetchAssociations();
    } catch (error) {
      console.error('Bulk verification error:', error);
      toast.error('Bulk verification failed');
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.verificationStatus !== 'all') {
        queryParams.append('verificationStatus', filters.verificationStatus);
      }
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }

      const url = `https://register.iwlf.in/api/admin/state-associations/export${
        queryParams.toString() ? `?${queryParams}` : ''
      }`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Export failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `state-associations-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleImageView = (imageUrl, title) => {
    setCurrentImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  const handleImageDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const toggleAssociationSelection = (associationId) => {
    setSelectedAssociations((prev) =>
      prev.includes(associationId)
        ? prev.filter((id) => id !== associationId)
        : [...prev, associationId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAssociations.length === associations.length) {
      setSelectedAssociations([]);
    } else {
      setSelectedAssociations(associations.map((a) => a._id));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleEditAssociation = () => {
    setEditingAssociation(true);
    setEditFormData({
      registrationNumber: selectedAssociation.registrationNumber || '',
      presidentEmail: selectedAssociation.president?.email || '',
      presidentMobile: selectedAssociation.president?.mobile || '',
      secretaryEmail: selectedAssociation.secretary?.email || '',
      secretaryMobile: selectedAssociation.secretary?.mobile || '',
      treasurerEmail: selectedAssociation.treasurer?.email || '',
      treasurerMobile: selectedAssociation.treasurer?.mobile || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editFormData.registrationNumber) {
      toast.error('Registration number is required');
      return;
    }

    try {
      setDeleting(true);
      await adminAPI.updateStateAssociationDetails(selectedAssociation._id, editFormData);
      toast.success('Association details updated successfully');
      setEditingAssociation(false);
      const response = await adminAPI.getStateAssociationDetails(selectedAssociation._id);
      setSelectedAssociation(response.data);
      fetchAssociations();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update details');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAssociation(false);
    setEditFormData({});
  };

  if (loading && associations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('https://') || url.startsWith('http://')) return url;
    return `https://register.iwlf.in${url}`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Container maxWidth="xl" sx={{ py: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#291e6c', mb: 1 }}>
            State Associations List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and verify state association registrations
          </Typography>
        </Box>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by registration number, email, president, secretary..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchInput('');
                        setFilters({ ...filters, search: '', page: 1 });
                      }}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <FilterList />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Filters
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Verification</InputLabel>
                    <Select
                      value={filters.verificationStatus}
                      label="Verification"
                      onChange={(e) => handleFilterChange('verificationStatus', e.target.value)}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    size="small"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    size="small"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setFilters({
                        verificationStatus: 'all',
                        startDate: '',
                        endDate: '',
                        search: '',
                        page: 1,
                        limit: 20,
                        sortBy: 'createdAt',
                        sortOrder: 'desc',
                      });
                      setSearchInput('');
                    }}
                    sx={{ height: '40px' }}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={handleExportCSV}
                    disabled={exporting}
                    startIcon={<GetApp />}
                    sx={{ height: '44px' }}
                  >
                    {exporting ? <>Exporting... <CircularProgress size={20} sx={{ ml: 1 }} /></> : 'Export CSV'}
                  </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<Search />}
                    sx={{ height: '44px' }}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Summary */}
          {pagination.totalRecords !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {associations.length} of {pagination.totalRecords} association(s)
              {filters.search && ` matching "${filters.search}"`}
            </Typography>
          )}

          {/* Bulk Actions */}
          {selectedAssociations.length > 0 && (
            <Card sx={{ mb: 3, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedAssociations.length} association(s) selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ThumbUp />}
                    onClick={() => setShowVerificationModal(true)}
                  >
                    Bulk Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<ThumbDown />}
                    onClick={() => setShowVerificationModal(true)}
                  >
                    Bulk Reject
                  </Button>
                  <Button variant="outlined" onClick={() => setSelectedAssociations([])}>
                    Clear Selection
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAssociations.length > 0 && selectedAssociations.length === associations.length}
                      onChange={toggleSelectAll}
                      indeterminate={selectedAssociations.length > 0 && selectedAssociations.length < associations.length}
                    />
                  </TableCell>
                  <TableCell>Registration No.</TableCell>
                  <TableCell>President</TableCell>
                  <TableCell>Secretary</TableCell>
                  <TableCell>Treasurer</TableCell>
                  <TableCell>Last Election</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {associations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box sx={{ py: 4 }}>
                        <Business sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="h6" color="text.secondary">
                          No associations found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your filters
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  associations.map((association) => (
                    <TableRow key={association._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAssociations.includes(association._id)}
                          onChange={() => toggleAssociationSelection(association._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {association.registrationNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {association.president?.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {association.president?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {association.secretary?.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {association.secretary?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {association.treasurer?.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {association.treasurer?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(association.lastElectionDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={association.verificationStatus.toUpperCase()}
                          color={getStatusColor(association.verificationStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(association.createdAt)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleViewDetails(association._id)}>
                          <Visibility />
                        </IconButton>
                        {association.verificationStatus === 'pending' && (
                          <>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleQuickApprove(association._id)}
                            >
                              <CheckCircle />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleQuickReject(association._id)}
                            >
                              <Cancel />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteAssociationId(association._id);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.totalRecords || 0}
            page={filters.page - 1}
            onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>

        {/* Details Modal */}
        {/* Details Modal - REPLACE THE ENTIRE DIALOG */}
<Dialog
  open={showModal}
  onClose={() => {
    setShowModal(false);
    setEditingAssociation(false);
  }}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    sx: { maxHeight: '90vh' }
  }}
>
  <DialogTitle>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Business sx={{ color: '#291e6c' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
          State Association Details
        </Typography>
      </Box>
      <IconButton
        onClick={() => {
          setShowModal(false);
          setEditingAssociation(false);
        }}
      >
        <Close />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent dividers>
    {selectedAssociation && (
      <Grid container spacing={3}>
        {/* Registration Details Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            backgroundColor: 'rgba(41, 30, 108, 0.05)', 
            p: 2, 
            borderRadius: 2,
            borderLeft: '4px solid #291e6c'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 2 }}>
              📋 Registration Information
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Registration Number"
            value={editingAssociation ? editFormData.registrationNumber : selectedAssociation.registrationNumber}
            onChange={(e) => setEditFormData({ ...editFormData, registrationNumber: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Badge sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Last Election Date"
            value={formatDate(selectedAssociation.lastElectionDate)}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Login Email"
            value={selectedAssociation.email}
            disabled
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Registration Certificate */}
        {selectedAssociation.registrationCertificate?.path && (
          <Grid item xs={12}>
            <Card sx={{ backgroundColor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: '#291e6c' }}>
                  <Download /> Registration Certificate
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleImageView(
                      selectedAssociation.registrationCertificate.path,
                      'Registration Certificate'
                    )}
                    startIcon={<Visibility />}
                  >
                    View
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => window.open(selectedAssociation.registrationCertificate.path, '_blank')}
                    startIcon={<Download />}
                  >
                    Download
                  </Button>
                </Box>
                {selectedAssociation.registrationCertificate.mimetype?.includes('image') && (
                  <Box 
                    component="img"
                    src={selectedAssociation.registrationCertificate.path}
                    alt="Registration Certificate"
                    sx={{ 
                      width: '100%', 
                      maxHeight: '300px', 
                      objectFit: 'contain',
                      mt: 2,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid #e0e0e0'
                    }}
                    onClick={() => handleImageView(
                      selectedAssociation.registrationCertificate.path,
                      'Registration Certificate'
                    )}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* President Details Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            backgroundColor: 'rgba(41, 30, 108, 0.05)', 
            p: 2, 
            borderRadius: 2,
            borderLeft: '4px solid #291e6c'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 0 }}>
              👤 President Details
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Name"
            value={selectedAssociation.president?.fullName}
            disabled
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            value={editingAssociation ? editFormData.presidentEmail : selectedAssociation.president?.email}
            onChange={(e) => setEditFormData({ ...editFormData, presidentEmail: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Mobile"
            value={editingAssociation ? editFormData.presidentMobile : selectedAssociation.president?.mobile}
            onChange={(e) => setEditFormData({ ...editFormData, presidentMobile: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="PAN Number"
            value={selectedAssociation.president?.pan}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Aadhar Number"
            value={selectedAssociation.president?.aadharNumber}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={selectedAssociation.president?.address}
            disabled
            size="small"
            multiline
            rows={2}
          />
        </Grid>

        {/* President Documents */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#291e6c' }}>
            President Documents
          </Typography>
          <Grid container spacing={2}>
            {/* PAN Card */}
            {selectedAssociation.president?.panCard?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      PAN Card
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.president.panCard.path}
                      alt="President PAN Card"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.president.panCard.path,
                        'President PAN Card'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.president.panCard.path,
                          'President PAN Card'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.president.panCard.path,
                          selectedAssociation.president.panCard.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Front */}
            {selectedAssociation.president?.aadharCardFront?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Front)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.president.aadharCardFront.path}
                      alt="President Aadhar Front"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.president.aadharCardFront.path,
                        'President Aadhar Card (Front)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.president.aadharCardFront.path,
                          'President Aadhar Card (Front)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.president.aadharCardFront.path,
                          selectedAssociation.president.aadharCardFront.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Back */}
            {selectedAssociation.president?.aadharCardBack?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Back)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.president.aadharCardBack.path}
                      alt="President Aadhar Back"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.president.aadharCardBack.path,
                        'President Aadhar Card (Back)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.president.aadharCardBack.path,
                          'President Aadhar Card (Back)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.president.aadharCardBack.path,
                          selectedAssociation.president.aadharCardBack.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Secretary Details Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            backgroundColor: 'rgba(41, 30, 108, 0.05)', 
            p: 2, 
            borderRadius: 2,
            borderLeft: '4px solid #291e6c'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 0 }}>
              👤 Secretary Details
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Name"
            value={selectedAssociation.secretary?.fullName}
            disabled
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            value={editingAssociation ? editFormData.secretaryEmail : selectedAssociation.secretary?.email}
            onChange={(e) => setEditFormData({ ...editFormData, secretaryEmail: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Mobile"
            value={editingAssociation ? editFormData.secretaryMobile : selectedAssociation.secretary?.mobile}
            onChange={(e) => setEditFormData({ ...editFormData, secretaryMobile: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="PAN Number"
            value={selectedAssociation.secretary?.pan}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Aadhar Number"
            value={selectedAssociation.secretary?.aadharNumber}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={selectedAssociation.secretary?.address}
            disabled
            size="small"
            multiline
            rows={2}
          />
        </Grid>

        {/* Secretary Documents */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#291e6c' }}>
            Secretary Documents
          </Typography>
          <Grid container spacing={2}>
            {/* PAN Card */}
            {selectedAssociation.secretary?.panCard?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      PAN Card
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.secretary.panCard.path}
                      alt="Secretary PAN Card"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.secretary.panCard.path,
                        'Secretary PAN Card'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.secretary.panCard.path,
                          'Secretary PAN Card'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.secretary.panCard.path,
                          selectedAssociation.secretary.panCard.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Front */}
            {selectedAssociation.secretary?.aadharCardFront?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Front)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.secretary.aadharCardFront.path}
                      alt="Secretary Aadhar Front"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.secretary.aadharCardFront.path,
                        'Secretary Aadhar Card (Front)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.secretary.aadharCardFront.path,
                          'Secretary Aadhar Card (Front)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.secretary.aadharCardFront.path,
                          selectedAssociation.secretary.aadharCardFront.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Back */}
            {selectedAssociation.secretary?.aadharCardBack?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Back)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.secretary.aadharCardBack.path}
                      alt="Secretary Aadhar Back"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.secretary.aadharCardBack.path,
                        'Secretary Aadhar Card (Back)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.secretary.aadharCardBack.path,
                          'Secretary Aadhar Card (Back)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.secretary.aadharCardBack.path,
                          selectedAssociation.secretary.aadharCardBack.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Treasurer Details Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            backgroundColor: 'rgba(41, 30, 108, 0.05)', 
            p: 2, 
            borderRadius: 2,
            borderLeft: '4px solid #291e6c'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 0 }}>
              👤 Treasurer Details
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Full Name"
            value={selectedAssociation.treasurer?.fullName}
            disabled
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            value={editingAssociation ? editFormData.treasurerEmail : selectedAssociation.treasurer?.email}
            onChange={(e) => setEditFormData({ ...editFormData, treasurerEmail: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Mobile"
            value={editingAssociation ? editFormData.treasurerMobile : selectedAssociation.treasurer?.mobile}
            onChange={(e) => setEditFormData({ ...editFormData, treasurerMobile: e.target.value })}
            disabled={!editingAssociation}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone sx={{ color: '#291e6c' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="PAN Number"
            value={selectedAssociation.treasurer?.pan}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Aadhar Number"
            value={selectedAssociation.treasurer?.aadharNumber}
            disabled
            size="small"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={selectedAssociation.treasurer?.address}
            disabled
            size="small"
            multiline
            rows={2}
          />
        </Grid>

        {/* Treasurer Documents */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#291e6c' }}>
            Treasurer Documents
          </Typography>
          <Grid container spacing={2}>
            {/* PAN Card */}
            {selectedAssociation.treasurer?.panCard?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      PAN Card
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.treasurer.panCard.path}
                      alt="Treasurer PAN Card"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.treasurer.panCard.path,
                        'Treasurer PAN Card'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.treasurer.panCard.path,
                          'Treasurer PAN Card'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.treasurer.panCard.path,
                          selectedAssociation.treasurer.panCard.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Front */}
            {selectedAssociation.treasurer?.aadharCardFront?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Front)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.treasurer.aadharCardFront.path}
                      alt="Treasurer Aadhar Front"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.treasurer.aadharCardFront.path,
                        'Treasurer Aadhar Card (Front)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.treasurer.aadharCardFront.path,
                          'Treasurer Aadhar Card (Front)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.treasurer.aadharCardFront.path,
                          selectedAssociation.treasurer.aadharCardFront.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Aadhar Back */}
            {selectedAssociation.treasurer?.aadharCardBack?.path && (
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: '#291e6c' }}>
                      Aadhar Card (Back)
                    </Typography>
                    <Box 
                      component="img"
                      src={selectedAssociation.treasurer.aadharCardBack.path}
                      alt="Treasurer Aadhar Back"
                      sx={{ 
                        width: '100%', 
                        height: '150px', 
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #e0e0e0'
                      }}
                      onClick={() => handleImageView(
                        selectedAssociation.treasurer.aadharCardBack.path,
                        'Treasurer Aadhar Card (Back)'
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageView(
                          selectedAssociation.treasurer.aadharCardBack.path,
                          'Treasurer Aadhar Card (Back)'
                        )}
                        startIcon={<Visibility />}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        fullWidth
                        variant="outlined"
                        onClick={() => handleImageDownload(
                          selectedAssociation.treasurer.aadharCardBack.path,
                          selectedAssociation.treasurer.aadharCardBack.filename
                        )}
                        startIcon={<Download />}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Verification Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            backgroundColor: 'rgba(41, 30, 108, 0.05)', 
            p: 2, 
            borderRadius: 2,
            borderLeft: '4px solid #291e6c'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 0 }}>
              ✅ Verification
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Verification Notes"
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            placeholder="Add notes for verification (optional)"
          />
        </Grid>

        {selectedAssociation.verificationDate && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Verified By"
                value={selectedAssociation.verifiedBy?.name || '-'}
                disabled
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Verification Date"
                value={formatDate(selectedAssociation.verificationDate)}
                disabled
                size="small"
              />
            </Grid>
            {selectedAssociation.verificationNotes && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Previous Verification Notes"
                  value={selectedAssociation.verificationNotes}
                  disabled
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    )}
  </DialogContent>
  <DialogActions sx={{ p: 2.5 }}>
    {selectedAssociation && (
      <>
        {editingAssociation ? (
          <>
            <Button onClick={handleCancelEdit} variant="outlined">
              Cancel Edit
            </Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={deleting}>
              {deleting ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setShowModal(false)} variant="outlined">
              Close
            </Button>
            <Button onClick={handleEditAssociation} variant="outlined" startIcon={<Edit />}>
              Edit Details
            </Button>
            {selectedAssociation.verificationStatus === 'pending' && (
              <>
                <Button
                  onClick={() => handleVerifyAssociation(selectedAssociation._id, 'rejected')}
                  color="error"
                  variant="outlined"
                  startIcon={<Cancel />}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handleVerifyAssociation(selectedAssociation._id, 'approved')}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircle />}
                >
                  Approve
                </Button>
              </>
            )}
          </>
        )}
      </>
    )}
  </DialogActions>
</Dialog>



        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this state association?</Typography>
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              ⚠️ Warning: This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button onClick={handleDeleteAssociation} color="error" variant="contained" disabled={deleting}>
              {deleting ? <><CircularProgress size={20} /> Deleting...</> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Verification Modal */}
        <Dialog
          open={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Bulk Verification</DialogTitle>
          <DialogContent>
            <Typography>You are about to verify {selectedAssociations.length} association(s).</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              placeholder="Add notes for bulk verification (optional)"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowVerificationModal(false)}>Cancel</Button>
            <Button
              onClick={() => handleBulkVerify('rejected')}
              color="error"
              variant="outlined"
              startIcon={<ThumbDown />}
            >
              Reject All
            </Button>
            <Button
              onClick={() => handleBulkVerify('approved')}
              color="success"
              variant="contained"
              startIcon={<ThumbUp />}
            >
              Approve All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Preview Modal */}
        <Dialog open={showImageModal} onClose={() => setShowImageModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {currentImage?.title}
            <IconButton
              onClick={() => setShowImageModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {currentImage && (
              <Box
                component="img"
                src={currentImage.url}
                alt={currentImage.title}
                sx={{ width: '100%', height: 'auto' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleImageDownload(currentImage?.url, currentImage?.title)} startIcon={<Download />}>
              Download
            </Button>
            <Button onClick={() => setShowImageModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default StateAssociationsList;
